import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Receipt, Zap } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrencyExact } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Stripe's published flat SEPA Direct Debit fee (docs.stripe.com/pricing/local-payment-methods,
// confirmed live) — an illustrative figure for the breakdown tooltip below, not
// a per-transaction lookup against the actual Stripe balance transaction.
const STRIPE_SEPA_FEE = 0.35;

// Mirrors yp/rent_schedule.py's resolve_rent — the rent in effect for `period`
// is the latest rent_changes entry whose effective_from <= period, since a
// contract's rent can change over time (e.g. an annual NRAU update).
export function resolveRent(changes, period, fallback) {
  let amount = fallback;
  for (const rc of [...(changes || [])].sort((a, b) => a.effective_from.localeCompare(b.effective_from))) {
    if (rc.effective_from <= period) amount = rc.amount;
  }
  return amount;
}

// Full month range for a contract — from start_date (or created_at, if the
// tenancy's start was never recorded) through end_date/today. No cap: this
// feeds the per-contract detail page, where "see everything" is the point;
// the condensed list view instead passes a single explicit month.
export function monthsForContract(contract) {
  const today = new Date();
  const end = contract.end_date ? new Date(contract.end_date) : today;
  const capEnd = end < today ? end : today;
  const origin = contract.start_date || contract.created_at;
  const capStart = origin ? new Date(origin) : capEnd;

  const months = [];
  let cursor = new Date(capEnd.getFullYear(), capEnd.getMonth(), 1);
  const floor = new Date(capStart.getFullYear(), capStart.getMonth(), 1);
  while (cursor >= floor) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
  }
  return months;
}

// One row of month chips for a contract — toggle paid/unpaid, download the
// receipt worksheet for that exact month. Used both for the condensed
// single-month preview on the Payments list and the full unbounded history
// on the per-contract detail page.
export default function RentTimeline({ contract, months }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: payments = [] } = useQuery({ queryKey: ["payments"], queryFn: async () => (await api.get("/payments")).data });

  const paidMap = new Map();
  for (const p of payments) paidMap.set(`${p.contract_id}:${p.period}`, p);

  const toggle = useMutation({
    mutationFn: ({ cid, period, paid }) => (paid
      ? api.delete(`/contracts/${cid}/mark-paid?period=${period}`)
      : api.post(`/contracts/${cid}/mark-paid?period=${period}`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: () => toast.error("Error"),
  });

  return (
    <div className="flex flex-wrap gap-2">
      {months.map((period) => {
        const payment = paidMap.get(`${contract.id}:${period}`);
        const paid = !!payment;
        const [y, m] = period.split("-");
        const periodRent = resolveRent(contract.rent_changes, period, contract.rent_amount);
        return (
          <div key={period} className="flex items-center gap-1" data-testid={`payment-chip-${contract.id}-${period}`}>
            <Button
              variant="outline" size="sm"
              onClick={() => toggle.mutate({ cid: contract.id, period, paid })}
              disabled={toggle.isPending}
              title={paid ? t("payments_paid") : t("payments_unpaid")}
              className={`rounded-sm gap-1.5 ${paid ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" : "text-muted-foreground"}`}
            >
              {paid ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
              {m}/{y} · {fmtCurrencyExact(periodRent)}
              {payment?.source === "stripe" && (
                <span title={
                  `${t("payments_auto_reconciled")} — ${t("payments_gross")}: ${fmtCurrencyExact(periodRent)} · ` +
                  `${t("payments_stripe_fee")}: ${fmtCurrencyExact(STRIPE_SEPA_FEE)} · ` +
                  `${t("payments_net_credited")}: ${fmtCurrencyExact(Math.max(0, periodRent - STRIPE_SEPA_FEE))}`
                }><Zap className="h-3.5 w-3.5" /></span>
              )}
            </Button>
            <Button variant="ghost" size="icon" title={t("recibo_worksheet")}
              onClick={() => window.open(`${API}/contracts/${contract.id}/recibo-worksheet?period=${period}`, "_blank")}
              className="h-8 w-8 text-muted-foreground hover:text-foreground">
              <Receipt className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
